import { useEffect } from 'react';

/**
 * FullscreenPreview — a near-full-viewport modal wrapping the
 * preview iframe. Keeps the user on the same screen (vs. opening a
 * new tab) so they can keep iterating with the chat on the side.
 *
 * Close: ESC, click on backdrop, or Close button.
 */
export function FullscreenPreview({
  onClose,
  title,
  children,
}: {
  onClose(): void;
  title?: string;
  children: React.ReactNode;
}): JSX.Element {
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fs-preview-backdrop" onClick={onClose}>
      <div className="fs-preview" onClick={(e) => e.stopPropagation()}>
        <header className="fs-preview-header">
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="badge badge--neutral">Full preview</span>
            {title && <span className="text-sm text-dim">{title}</span>}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <span className="text-mute text-xs">Press ESC or click outside to close</span>
            <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className="fs-preview-body">{children}</div>
      </div>
      <style>{`
        .fs-preview-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.72);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 1200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fs-fade 200ms ease-out;
        }
        .fs-preview {
          background: var(--color-surface-1);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-xl);
          width: 100%;
          height: 100%;
          max-width: 1480px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: var(--shadow-xl);
          animation: fs-scale 220ms var(--ease-out);
        }
        .fs-preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid var(--color-border-subtle);
          background: var(--color-surface-1);
          flex-shrink: 0;
        }
        .fs-preview-body {
          flex: 1;
          min-height: 0;
          background: var(--color-surface-2);
          display: flex;
          align-items: stretch;
          justify-content: center;
          padding: 24px;
        }
        .fs-preview-body > iframe,
        .fs-preview-body > * {
          width: 100%;
          height: 100%;
          border: 0;
          border-radius: var(--radius-md);
          background: #fff;
          box-shadow: 0 8px 28px rgba(0,0,0,0.25);
        }
        @keyframes fs-fade {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes fs-scale {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
